<template>
  <div
    v-if="(!conversationSize && isFetchingList) || contactUIFlags.isUpdating"
    class="flex flex-1 items-center h-full bg-black-25 justify-center"
    :class="{ dark: prefersDarkMode }"
  >
    <spinner size="large" />
  </div>
  <div
    v-else
    class="flex flex-col justify-end h-full"
    :class="{
      'is-mobile': isMobile,
      'is-widget-right': isRightAligned,
      'is-bubble-hidden': hideMessageBubble,
      'is-flat-design': isWidgetStyleFlat,
      dark: prefersDarkMode,
    }"
  >
    <router-view />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { setHeader } from 'widget/helpers/axios';
import addHours from 'date-fns/addHours';
import { IFrameHelper, RNHelper } from 'widget/helpers/utils';
import availabilityMixin from 'widget/mixins/availability';
import configMixin from './mixins/configMixin';
import conversationMixin from './mixins/conversationMixin';
import { getLastConversationLSKey, getLocale } from './helpers/urlParamsHelper';
import { isEmptyObject } from 'widget/helpers/utils';
import Spinner from 'shared/components/Spinner.vue';
import routerMixin from './mixins/routerMixin';
import {
  getExtraSpaceToScroll,
  loadedEventConfig,
} from './helpers/IframeEventHelper';
import {
  ON_AGENT_MESSAGE_RECEIVED,
  ON_CAMPAIGN_MESSAGE_CLICK,
  ON_UNREAD_MESSAGE_CLICK,
} from './constants/widgetBusEvents';

import { SDK_SET_BUBBLE_VISIBILITY } from '../shared/constants/sharedFrameEvents';
import { LocalStorage } from '../shared/helpers/localStorage';

const LOADED_LANGUAGES = ['en'];

export default {
  name: 'App',
  components: {
    Spinner,
  },
  mixins: [availabilityMixin, configMixin, routerMixin, conversationMixin],
  data() {
    return {
      campaignsSnoozedTill: undefined,
    };
  },
  computed: {
    ...mapGetters({
      activeCampaign: 'campaign/getActiveCampaign',
      campaigns: 'campaign/getCampaigns',
      conversationSize: 'conversation/getConversationSize',
      currentUser: 'contacts/getCurrentUser',
      hasFetched: 'agent/getHasFetched',
      hideMessageBubble: 'appConfig/getHideMessageBubble',
      forceManualLinkManagement: 'appConfig/getForceManualLinkManagement',
      isFetchingList: 'conversation/getIsFetchingList',
      isRightAligned: 'appConfig/isRightAligned',
      isWidgetOpen: 'appConfig/getIsWidgetOpen',
      escapeUnreadView: 'appConfig/getEscapeUnreadView',
      autoOpenUnreadConversation: 'appConfig/getAutoOpenUnreadConversation',
      isMobile: 'appConfig/isMobile',
      darkMode: 'appConfig/darkMode',
      unreadMessageCount: 'conversation/getUnreadMessageCount',
      lastConversation: 'conversation/getLastConversation',
      conversationParams: 'conversationAttributes/getConversationParams',
      isWidgetStyleFlat: 'appConfig/isWidgetStyleFlat',
      allConversations: 'conversation/getAllConversations',
      contactUIFlags: 'contacts/getUIFlags',
    }),
    isIFrame() {
      return IFrameHelper.isIFrame();
    },
    isRNWebView() {
      return RNHelper.isRNWebView();
    },
    prefersDarkMode() {
      const isOSOnDarkMode =
        this.darkMode === 'auto' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      return isOSOnDarkMode || this.darkMode === 'dark';
    },
  },
  watch: {
    activeCampaign() {
      this.setCampaignView();
    },
    unreadMessageCount() {
      this.handleUnreadNotificationDot();
    },
  },
  mounted() {
    window.logger.logUserAction('info', 'App mounted');

    window.addEventListener('online', () => {
      this.replaceRoute('home');
      window.logger.logUserAction('info', 'User back online');
    });
    window.addEventListener('offline', () => {
      window.logger.logUserAction('info', 'User is offline');
    });

    const { websiteToken, locale } = window.hooryWebChannel;

    /**
     * Fix for when there was another widget with conversation before
     */
    const prevToken = LocalStorage.get('__hoory_wt') || '';
    if (prevToken !== websiteToken) {
      this.$store.dispatch(
        'conversationAttributes/clearConversationAttributes'
      );
      LocalStorage.set('__hoory_wt', websiteToken);
    }

    this.setLocale(locale);
    this.setWidgetColorVariable();
    setHeader(window.authToken);
    if (this.isIFrame) {
      this.registerListeners();
      this.sendLoadedEvent();
    } else {
      this.$store.dispatch('appConfig/toggleWidgetOpen', true);
      this.fetchOldConversations();
      this.fetchAvailableAgents(websiteToken);
      this.setLocale(getLocale(window.location.search));
    }
    if (this.isRNWebView) {
      this.registerListeners();
      this.sendRNWebViewLoadedEvent();
    }
    // TODO: remove this
    this.$store.dispatch('conversationAttributes/getAttributes');
    this.registerUnreadEvents();
    this.registerCampaignEvents();

    const widgetSettings = JSON.parse(
      window.hooryWebChannel.widget_settings || '{}'
    );

    if (widgetSettings?.darkMode) {
      this.$store.dispatch('appConfig/setDarkMode', widgetSettings?.darkMode);
    }
  },
  methods: {
    ...mapActions('appConfig', [
      'setAppConfig',
      'setReferrerHost',
      'setWidgetColor',
      'setBubbleVisibility',
    ]),
    ...mapActions('conversation', ['fetchOldConversations', 'setUserLastSeen']),
    ...mapActions('campaign', [
      'initCampaigns',
      'executeCampaign',
      'resetCampaign',
    ]),
    ...mapActions('agent', ['fetchAvailableAgents']),
    scrollConversationToBottom() {
      const container = this.$el.querySelector('.conversation-wrap');
      container.scrollTop = container.scrollHeight;
    },
    setBubbleLabel() {
      IFrameHelper.sendMessage({
        event: 'setBubbleLabel',
        label: this.$t('BUBBLE.LABEL'),
      });
    },
    setIframeHeight(isFixedHeight) {
      this.$nextTick(() => {
        const extraHeight = getExtraSpaceToScroll();
        IFrameHelper.sendMessage({
          event: 'updateIframeHeight',
          isFixedHeight,
          extraHeight,
        });
      });
    },
    setWidgetColorVariable() {
      const { widgetColor } = window.hooryWebChannel;
      this.setWidgetColor(widgetColor);
      document.documentElement.style.setProperty('--widget-color', widgetColor);
    },
    async setLocale(localeWithVariation) {
      window.logger.logUserAction('info', 'Changed locale');

      if (!localeWithVariation) return;
      const { enabledLanguages } = window.hooryWebChannel;
      const localeWithoutVariation = localeWithVariation.split('_')[0];
      const hasLocaleWithoutVariation = enabledLanguages.some(
        lang => lang.iso_639_1_code === localeWithoutVariation
      );
      const hasLocaleWithVariation = enabledLanguages.some(
        lang => lang.iso_639_1_code === localeWithVariation
      );

      let lang = 'en';
      if (hasLocaleWithVariation) {
        lang = localeWithVariation;
      } else if (hasLocaleWithoutVariation) {
        lang = localeWithoutVariation;
      }

      const RTL_LANGUAGES = ['ar', 'he', 'fa'];
      document.documentElement.dir = RTL_LANGUAGES.includes(lang)
        ? 'rtl'
        : 'ltr';

      try {
        if (!LOADED_LANGUAGES.includes(lang)) {
          const messages = await import(
            /* webpackChunkName: "lang-[request]" */ `./i18n/${lang}.js`
          );
          this.$root.$i18n.setLocaleMessage(lang, messages.default);
          LOADED_LANGUAGES.push(lang);
        }

        this.$root.$i18n.locale = lang;
      } catch (err) {
        window.logger.logUserAction(
          'error',
          `Failed to load language: ${lang}`,
          err
        );
      }
    },
    registerUnreadEvents() {
      bus.$on(ON_AGENT_MESSAGE_RECEIVED, data => {
        const { name: routeName } = this.$route;
        if ((this.isWidgetOpen || !this.isIFrame) && routeName === 'messages') {
          this.$store.dispatch('conversation/readConversationMessages', {
            conversationId: window.hooryCurrentConversationDid,
          });

          this.$store.dispatch('conversation/setUserLastSeen');
        }

        if (!this.isWidgetOpen) {
          this.$store.dispatch('conversation/getConversationList');
        }

        this.setUnreadView();

        /**
         * If auto open unread conversation is enabled
         */
        if (
          this.autoOpenUnreadConversation &&
          !this.isWidgetOpen &&
          data.conversation
        ) {
          this.$store.dispatch('conversationAttributes/set', {
            id: data.conversation.id,
            did: data.conversation.display_id,
            status: data.conversation.status,
            in_queue: data.conversation.in_queue,
            customAttributes: data.conversation.custom_attributes,
            archived: false,
          });

          this.replaceRoute(
            `messages`,
            {
              conversation: data.conversation.id,
              conversationDid: data.conversation.display_id,
            },
            true
          ).then(() => {
            IFrameHelper.sendMessage({
              event: 'toggleBubble',
              toggleValue: 'open',
            });
            this.unsetUnreadView();
          });
        }
      });
      bus.$on(ON_UNREAD_MESSAGE_CLICK, () => {
        this.replaceRoute(
          'messages',
          {
            conversation: this.lastUnreadConversation.id,
            conversationDid: this.lastUnreadConversation.display_id,
            conversationStatus: this.lastUnreadConversation.conversationStatus,
            in_queue: this.lastUnreadConversation.in_queue,
            archived: this.lastUnreadConversation.archived,
            customAttributes:
              this.lastUnreadConversation.custom_attributes ||
              this.lastUnreadConversation.customAttributes,
          },
          true
        ).then(() => this.unsetUnreadView());
      });
    },
    registerCampaignEvents() {
      bus.$on(ON_CAMPAIGN_MESSAGE_CLICK, () => {
        window.logger.logUserAction('info', 'Campaign clicked');
        if (this.shouldShowPreChatForm) {
          this.replaceRoute('prechat-form');
        } else {
          bus.$emit('execute-campaign', { campaignId: this.activeCampaign.id });
        }
        this.unsetUnreadView();
      });

      bus.$on('execute-campaign', campaignDetails => {
        window.logger.logUserAction(
          'info',
          'Executed campaign',
          campaignDetails
        );
        const { customAttributes, campaignId } = campaignDetails;

        // add campaign id to local storage
        const camaignIds = LocalStorage.get('__hoory_cids') || [];
        camaignIds.push(campaignId);
        LocalStorage.set('__hoory_cids', camaignIds);

        const { websiteToken } = window.hooryWebChannel;
        this.executeCampaign({ campaignId, websiteToken, customAttributes });
        this.replaceRoute('messages');
      });
      bus.$on('snooze-campaigns', () => {
        window.logger.logUserAction('info', 'Snoozed campaigns');

        const expireBy = addHours(new Date(), 1);
        this.campaignsSnoozedTill = Number(expireBy);
      });
    },
    setCampaignView() {
      const { activeCampaign } = this;
      const alreadyReadCampaign = LocalStorage.get('__hoory_cids') || [];
      const shouldSnoozeCampaign =
        this.campaignsSnoozedTill && this.campaignsSnoozedTill > Date.now();
      const isCampaignReadyToExecute =
        !isEmptyObject(activeCampaign) &&
        !shouldSnoozeCampaign &&
        !alreadyReadCampaign.includes(activeCampaign.id);

      if (this.isIFrame && isCampaignReadyToExecute) {
        this.replaceRoute('campaigns').then(() => {
          this.setIframeHeight(true);
          IFrameHelper.sendMessage({ event: 'setUnreadMode' });
        });
      } else if (this.$route.name === 'campaigns') {
        setTimeout(() => {
          this.replaceRoute('home');
          this.unsetUnreadView();
        }, 100);
      }
    },
    setUnreadView() {
      const { unreadMessageCount } = this;
      const urlParams = new URLSearchParams(window.location.search);
      const urlShowUnreadBubble = urlParams.get('showUnreadBubble');

      if (
        this.isIFrame &&
        !this.isWidgetOpen &&
        unreadMessageCount > 0 &&
        urlShowUnreadBubble !== '0'
      ) {
        if (!this.escapeUnreadView) {
          this.replaceRoute('unread-messages').then(() => {
            this.setIframeHeight(true);
            IFrameHelper.sendMessage({ event: 'setUnreadMode' });
          });
        }
        this.handleUnreadNotificationDot();
      }
    },
    unsetUnreadView() {
      if (this.isIFrame) {
        IFrameHelper.sendMessage({ event: 'resetUnreadMode' });
        this.setIframeHeight(false);
        this.handleUnreadNotificationDot();
      }
    },
    handleUnreadNotificationDot() {
      const { unreadMessageCount } = this;
      if (this.isIFrame) {
        IFrameHelper.sendMessage({
          event: 'handleNotificationDot',
          unreadMessageCount,
        });
      }
    },
    createWidgetEvents(message) {
      const { eventName } = message;
      const isWidgetTriggerEvent = eventName === 'webwidget.triggered';
      if (
        isWidgetTriggerEvent &&
        ['unread-messages', 'campaigns'].includes(this.$route.name)
      ) {
        return;
      }
      this.$store.dispatch('events/create', { name: eventName });
    },
    registerListeners() {
      const { websiteToken } = window.hooryWebChannel;
      window.addEventListener('message', e => {
        if (!IFrameHelper.isAValidEvent(e)) {
          return;
        }
        const message = IFrameHelper.getMessage(e);

        if (message.event === 'config-set') {
          window.logger.logUserAction('info', 'Sdk config set', message);

          this.setLocale(message.locale);
          this.setBubbleLabel();
          this.fetchOldConversations().then(() => this.setUnreadView());
          this.fetchAvailableAgents(websiteToken);
          this.setAppConfig(message);
          this.$store.dispatch('contacts/get');
          this.setCampaignReadData(message.campaignsSnoozedTill);
        } else if (message.event === 'widget-visible') {
          this.scrollConversationToBottom();
        } else if (message.event === 'change-url') {
          const { referrerURL, referrerHost } = message;
          this.initCampaigns({
            currentURL: referrerURL,
            websiteToken,
            isInBusinessHours: this.isInBusinessHours,
          });
          window.referrerURL = referrerURL;
          this.setReferrerHost(referrerHost);
        } else if (message.event === 'toggle-close-button') {
          this.$store.dispatch('appConfig/setIsMobile', message.isMobile);
        } else if (message.event === 'window-size-change') {
          this.$store.dispatch('appConfig/setWindowSize', message);
        } else if (message.event === 'push-event') {
          this.createWidgetEvents(message);
        } else if (message.event === 'set-label') {
          this.$store.dispatch('conversationLabels/create', message.label);
        } else if (message.event === 'remove-label') {
          this.$store.dispatch('conversationLabels/destroy', message.label);
        } else if (message.event === 'set-user') {
          this.$store.dispatch('contacts/setUser', message);
        } else if (message.event === 'set-custom-attributes') {
          this.$store.dispatch(
            'contacts/setCustomAttributes',
            message.customAttributes
          );
        } else if (message.event === 'delete-custom-attribute') {
          this.$store.dispatch(
            'contacts/deleteCustomAttribute',
            message.customAttribute
          );
        } else if (message.event === 'set-conversation-custom-attributes') {
          this.$store.dispatch(
            'conversation/setCustomAttributes',
            message.customAttributes
          );
        } else if (message.event === 'delete-conversation-custom-attribute') {
          this.$store.dispatch(
            'conversation/deleteCustomAttribute',
            message.customAttribute
          );
        } else if (message.event === 'set-locale') {
          this.setLocale(message.locale);
          this.setBubbleLabel();
        } else if (message.event === 'show-conversation') {
          const { conversationId } = message;
          this.fullSwitchToConversation(conversationId);
        } else if (message.event === 'toggle-open') {
          this.$store.dispatch('appConfig/toggleWidgetOpen', message.isOpen);
          const hasToLogin =
            window.hooryWebChannel.enforceIdentityUrl &&
            !this.currentUser.identifier;
          const lastSavedConversation = LocalStorage.get(
            getLastConversationLSKey()
          );
          const shouldShowMessageView =
            ['home'].includes(this.$route.name) &&
            message.isOpen &&
            !!lastSavedConversation.id &&
            !hasToLogin;
          const shouldShowHomeView =
            !lastSavedConversation &&
            !lastSavedConversation.id &&
            !message.isOpen &&
            ['unread-messages', 'campaigns'].includes(this.$route.name);

          /**
           * Force update conversation when widget is opened
           */
          if (typeof message.conversation !== 'undefined') {
            if (typeof message.conversation !== 'object') {
              this.fullSwitchToConversation(message.conversation);
            } else {
              this.replaceRoute(
                'messages',
                {
                  conversation: message.conversation.id,
                  conversationDid: message.conversation.display_id,
                  conversationStatus: message.conversation.status,
                  in_queue: message.conversation.in_queue,
                  archived: message.conversation.archived,
                  customAttributes: message.conversation.custom_attributes,
                },
                true
              );
            }
            return;
          }

          if (this.$route.name === 'messages' && this.conversationParams.id) {
            this.updateConversation({
              id: this.conversationParams.id,
              did: this.conversationParams.did,
              status: this.conversationParams.status,
              in_queue: this.conversationParams.in_queue,
              archived: this.conversationParams.archived,
              customAttributes: this.conversationParams.customAttributes,
            });
          }

          if (message.openNewConversation) {
            this.cleanCurrentConversation();
            this.replaceRoute('messages', {}, true);
          } else if (message.openLastConversation) {
            this.replaceRoute(
              'messages',
              {
                conversation: this.lastConversation.id,
                conversationDid: this.lastConversation.display_id,
                conversationStatus: this.lastConversation.status,
                in_queue: this.lastConversation.in_queue,
                archived: this.lastConversation.archived,
                customAttributes:
                  this.lastConversation.custom_attributes ||
                  this.lastConversation.custom_attributes,
              },
              true
            );
          } else if (shouldShowMessageView) {
            this.replaceRoute(
              'messages',
              {
                conversation: lastSavedConversation.id,
                conversationDid: lastSavedConversation.display_id,
                conversationStatus: lastSavedConversation.status,
                in_queue: lastSavedConversation.in_queue,
                archived: lastSavedConversation.archived,
                customAttributes:
                  lastSavedConversation.custom_attributes ||
                  lastSavedConversation.customAttributes,
              },
              true
            );
          } else if (shouldShowHomeView) {
            this.$store.dispatch('conversation/setUserLastSeen');
            this.unsetUnreadView();
            this.replaceRoute('home', {}, true);
          }
          if (!message.isOpen) {
            this.resetCampaign();
          }
        } else if (message.event === SDK_SET_BUBBLE_VISIBILITY) {
          this.setBubbleVisibility(message.hideMessageBubble);
        }
      });

      window.addEventListener('click', e => {
        // if target is an Anchor tag and is a child of .message-content
        if (
          e.target.tagName === 'A' &&
          e.target.closest('.message-content') &&
          this.forceManualLinkManagement
        ) {
          e.preventDefault();
          e.stopPropagation();

          IFrameHelper.sendMessage({
            event: 'onLinkClick',
            data: {
              href: e.target.href,
            },
          });
        }
      });
    },
    sendLoadedEvent() {
      IFrameHelper.sendMessage(loadedEventConfig());
    },
    sendRNWebViewLoadedEvent() {
      RNHelper.sendMessage(loadedEventConfig());
    },
    setCampaignReadData(snoozedTill) {
      if (snoozedTill) {
        this.campaignsSnoozedTill = Number(snoozedTill);
      }
    },
    fullSwitchToConversation(conversationDisplayId) {
      this.$store.dispatch('conversation/getConversationList').then(() => {
        const conversation = this.allConversations?.find(
          c => c.display_id === conversationDisplayId
        );
        if (conversation) {
          // set conversation attributes
          this.$store.dispatch('conversationAttributes/set', {
            id: conversation.id,
            did: conversation.display_id,
            in_queue: conversation.in_queue,
            archived: conversation.archived,
            status: conversation.status,
            customAttributes: conversation.custom_attributes,
          });

          // go to conversation
          this.replaceRoute(
            'messages',
            {
              conversation: conversation.id,
              conversationDid: conversation.display_id,
              conversationStatus: conversation.status,
              in_queue: conversation.in_queue,
              archived: conversation.archived,
              customAttributes: conversation.custom_attributes,
            },
            true
          );
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import '~widget/assets/scss/woot.scss';
</style>
